import * as React from 'react';
import { type ReactNode, createContext, useCallback, useEffect, useState } from 'react';

import { useAutoMarkSetUpLearningPlanGoalAsCompleted } from 'bundles/daily-goals/hooks/useAutoMarkSetUpLearningPlanGoalAsCompleted';
import useQueryDailyGoals from 'bundles/daily-goals/hooks/useQueryDailyGoals';
import type { DailyGoal, DailyGoalTypeNameString, LearningAssistantMessage } from 'bundles/daily-goals/types';
import { processLearningAssistantMessages } from 'bundles/daily-goals/utils/dailyGoalLearningAssistantUtils';
import { isAllDailyGoalsCompleted } from 'bundles/daily-goals/utils/dailyGoalsUtils';
import type { LearningAssistanceMessage } from 'bundles/learning-assistant/types/RealtimeMessages';

type DailyGoalsDataProviderProps = {
  activeCourseId: string | undefined;
  moduleId: string | undefined;
  nextModuleResourcePath: string | undefined;
  messages: LearningAssistanceMessage[];
  children: ReactNode;
};

export type DailyGoalsDataContextProps = {
  activeCourseId: string | undefined;
  dailyGoals?: Array<DailyGoal>;
  dayOfLearning?: number;
  dailyGoalsToastIds?: DailyGoalTypeNameString[];
  setDailyGoalsToastIds: React.Dispatch<React.SetStateAction<DailyGoalTypeNameString[] | undefined>>;
  pushToastId: (newToastId: DailyGoalTypeNameString) => void;
  removeFirstToastId: () => void;
  allDailyGoalsCompleted: boolean;
  loading: boolean;
  moduleCompletionLearningAssistantMessage: LearningAssistantMessage | undefined;
  setModuleCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistantMessage | undefined>
  >;
  courseCompletionLearningAssistantMessage: LearningAssistantMessage | undefined;
  setCourseCompletionLearningAssistantMessage: React.Dispatch<
    React.SetStateAction<LearningAssistantMessage | undefined>
  >;
  nextModuleResourcePath: string | undefined;
  showAllDailyGoalsCompletedModal: boolean;
  setShowAllDailyGoalsCompletedModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const DailyGoalsDataContext = createContext<DailyGoalsDataContextProps>({
  activeCourseId: undefined,
  dailyGoals: undefined,
  dayOfLearning: undefined,
  dailyGoalsToastIds: undefined,
  setDailyGoalsToastIds: () => {},
  pushToastId: () => {},
  removeFirstToastId: () => {},
  allDailyGoalsCompleted: false,
  loading: false,
  moduleCompletionLearningAssistantMessage: undefined,
  setModuleCompletionLearningAssistantMessage: () => {},
  courseCompletionLearningAssistantMessage: undefined,
  setCourseCompletionLearningAssistantMessage: () => {},
  nextModuleResourcePath: undefined,
  showAllDailyGoalsCompletedModal: false,
  setShowAllDailyGoalsCompletedModal: () => {},
});

// Export the context for testing
export { DailyGoalsDataContext };

export const DailyGoalsDataProvider = ({
  activeCourseId,
  moduleId,
  children,
  messages,
  nextModuleResourcePath,
}: DailyGoalsDataProviderProps) => {
  const { dailyGoals: queryDailyGoals, dayOfLearning, loading } = useQueryDailyGoals(activeCourseId);
  const [dailyGoals, setDailyGoals] = useState<Array<DailyGoal> | undefined>(queryDailyGoals);
  const [allDailyGoalsCompleted, setAllDailyGoalsCompleted] = useState(isAllDailyGoalsCompleted(queryDailyGoals));
  const [dailyGoalsToastIds, setDailyGoalsToastIds] = useState<DailyGoalTypeNameString[] | undefined>(undefined);
  const [moduleCompletionLearningAssistantMessage, setModuleCompletionLearningAssistantMessage] = useState<
    LearningAssistantMessage | undefined
  >(undefined);
  const [courseCompletionLearningAssistantMessage, setCourseCompletionLearningAssistantMessage] = useState<
    LearningAssistantMessage | undefined
  >(undefined);
  const [showAllDailyGoalsCompletedModal, setShowAllDailyGoalsCompletedModal] = useState(false);

  const pushToastId = useCallback<(newToastId: DailyGoalTypeNameString) => void>(
    (newToastId) => {
      setDailyGoalsToastIds((prevToastIds) => {
        if (!prevToastIds) return [newToastId];
        return [...prevToastIds, newToastId];
      });
    },
    [setDailyGoalsToastIds]
  );

  const removeFirstToastId = useCallback<() => void>(() => {
    setDailyGoalsToastIds((prevToastIds) => {
      if (!prevToastIds) return prevToastIds;
      return prevToastIds.slice(1);
    });
  }, [setDailyGoalsToastIds]);

  // Update local state when query data changes
  useEffect(() => {
    if (queryDailyGoals) {
      setDailyGoals([...queryDailyGoals]);
    }
  }, [queryDailyGoals]);

  // Process learning assistant messages and update daily goals
  useEffect(() => {
    if (messages?.length > 0) {
      processLearningAssistantMessages({
        messages,
        currentGoals: dailyGoals ?? [],
        moduleId,
        setDailyGoals,
        setModuleCompletionLearningAssistantMessage,
        setCourseCompletionLearningAssistantMessage,
        pushToastId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this effect when messages change
  }, [messages]);

  // Check if all daily goals are completed whenever dailyGoals changes
  useEffect(() => {
    if (dailyGoals) {
      setAllDailyGoalsCompleted(isAllDailyGoalsCompleted(dailyGoals));
    }
  }, [dailyGoals]);

  useAutoMarkSetUpLearningPlanGoalAsCompleted({
    activeCourseId,
    dailyGoals,
    pushToastId,
  });

  return (
    <DailyGoalsDataContext.Provider
      value={{
        dailyGoals,
        dayOfLearning,
        dailyGoalsToastIds,
        setDailyGoalsToastIds,
        pushToastId,
        removeFirstToastId,
        allDailyGoalsCompleted,
        loading,
        moduleCompletionLearningAssistantMessage,
        setModuleCompletionLearningAssistantMessage,
        courseCompletionLearningAssistantMessage,
        setCourseCompletionLearningAssistantMessage,
        nextModuleResourcePath,
        showAllDailyGoalsCompletedModal,
        setShowAllDailyGoalsCompletedModal,
        activeCourseId,
      }}
    >
      {children}
    </DailyGoalsDataContext.Provider>
  );
};

export const useDailyGoalsData = () => {
  const context = React.useContext(DailyGoalsDataContext);

  if (context === undefined) {
    throw new Error('useDailyGoalsData must be used within a DailyGoalsDataProvider');
  }

  return context;
};
